import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import {
  FlaggedLocation,
  IAccessPoint,
  IAllLocationsResponse,
  ILocOrgItem,
  IShareLocationWithTargets,
  IShareLocationWithTargetsResponse,
  LocationByUser,
  LocationDetails,
  LocationType
} from '../../types/location/location';
import { PropsDataResponse } from '../../types/apiCommonResponse/apiCommonResponse';
import customFetchBase from '../expiredToken/expiredToken';
import { UsersByLocation } from '../../types/users';

const customFetch = customFetchBase(config.baseUrlDealers);

export const apiLocations = createApi({
  reducerPath: 'apiLocations',
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getLocations: builder.mutation<
      IAllLocationsResponse,
      {
        page: number;
        size: number;
        additionalParams: {
          filterByLabel?: string;
          filterByOwner?: string;
          filterBySharedWith?: string;
          order?: 'asc' | 'desc';
          orderBy?: string;
        };
      }
    >({
      query: (payload) => ({
        url: 'api/Location/AllLocations',
        method: 'GET',
        params: {
          page: payload.page,
          size: payload.size,
          ...payload.additionalParams
        }
      })
    }),
    getFolders: builder.mutation<PropsDataResponse<any>, {}>({
      query: () => ({
        url: 'api/Location/UserFolderTrees',
        method: 'GET'
      })
    }),
    addFolders: builder.mutation<PropsDataResponse<any>, { name: string }>({
      query: (data) => ({
        url: '/api/Location/Folder',
        method: 'POST',
        body: {
          id: 0,
          name: data.name,
          parentId: 0,
          enabled: true,
          lastUpdate: '2023-11-01T21:02:19.957Z',
          folders: ['string'],
          locations: [
            {
              id: 0,
              folderId: 0,
              locationId: 0,
              enabled: true,
              lastUpdate: '2023-11-01T21:02:19.957Z',
              locationDetails: {
                id: 0,
                description: 'string',
                customerId: 0,
                timeZoneId: 0,
                trimNonProductionTime: true,
                enabled: true,
                subscriptionId: 0
              }
            }
          ]
        }
      })
    }),
    getLocationsUsersByLocationId: builder.mutation<UsersByLocation[], {}>({
      query: (locationId: number) => ({
        url: `api/Location/GetLocationUsersByLocationId/${locationId}`,
        method: 'GET'
      })
    }),
    addUserToLocation: builder.mutation<
      {},
      {
        UserId: number;
        LocationId: number;
        LocationAlias: string;
        LocationRoleId: number;
      }
    >({
      query: (data) => ({
        url: '/api/Location/ShareLocation',
        method: 'POST',
        body: data
      })
    }),
    deleteUserFromLocation: builder.mutation<{}, {}>({
      query: (userLocationId: number) => ({
        url: `api/Location/DeleteLocationUser/${userLocationId}`,
        method: 'DELETE'
      })
    }),
    getLocationsByLoggedUser: builder.mutation<LocationByUser[], {}>({
      query: () => ({
        url: `api/Location/GetLoggedUserLocations`,
        method: 'GET'
      })
    }),
    getLocationDetailsByLocationId: builder.mutation<LocationDetails, number>({
      query: (id) => ({
        url: `/api/Location/${id}`,
        method: 'GET'
      })
    }),
    getUserLastLocations: builder.mutation<
      {
        id: number;
        name: string;
      }[],
      { userId: number | null; numLocations: number }
    >({
      query: (payload) => ({
        url: `/api/Location/UserLastSelectedLocation/${payload.userId}/${payload.numLocations}`,
        method: 'GET'
      })
    }),
    postUserSelectLocation: builder.mutation<
      null,
      { userId: number | null; locationId: number | null }
    >({
      query: (payload) => ({
        url: `/api/Location/UserSelectedLocation`,
        method: 'POST',
        body: payload
      })
    }),
    deleteLocation: builder.mutation<null, number[]>({
      query: (locationIds) => ({
        url: `api/Location?ids=${locationIds.join(',')}`,
        method: `DELETE`
      })
    }),
    shareLocationWithTargets: builder.mutation<
      IShareLocationWithTargetsResponse[],
      IShareLocationWithTargets
    >({
      query: (data) => ({
        url: `api/Location/ShareLocationUsingTargets`,
        method: 'POST',
        body: data
      })
    }),
    getFlaggedLocations: builder.mutation<FlaggedLocation[], void>({
      query: () => ({
        url: `/api/Location/FlaggedLocations`,
        method: 'GET'
      })
    }),
    getLoggedUserLocationsAndOrganizations: builder.mutation<
      ILocOrgItem[],
      null
    >({
      query: () => ({
        url: `api/Location/GetLoggedUserLocationsAndOrganizations`,
        method: 'GET'
      })
    }),
    getLocationsAndOrganizationsByUser: builder.mutation<ILocOrgItem[], number>(
      {
        query: (userId) => ({
          url: `api/Location/GetLocationsAndOrganizationsByUser`,
          params: {
            userId
          },
          method: 'GET'
        })
      }
    ),
    updateLocationLatLong: builder.mutation<
      null,
      { locationId: number; lat: number; lon: number }
    >({
      query: (data) => ({
        url: `api/Location/${data.locationId}/UpdateGPS`,
        method: 'PUT',
        body: {
          latitude: data.lat,
          longitude: data.lon
        }
      })
    }),
    incrementAllTablesVersion: builder.mutation<null, number>({
      query: (locationId) => ({
        url: `/api/Location/IncrementAllTablesVersion/${locationId}`,
        method: 'PUT'
      })
    }),
    getLocationTypes: builder.mutation<LocationType[], void>({
      query: () => ({
        url: `/api/Location/LocationTypes`,
        method: 'GET'
      })
    }),
    updateLocationType: builder.mutation<
      null,
      { locationId: number; locationType: number }
    >({
      query: (data) => ({
        url: `/api/Location/LocationType`,
        method: 'PUT',
        body: data
      })
    }),
    getAccessPointsByLocation: builder.mutation<IAccessPoint[], number>({
      query: (locationId) => ({
        url: `/api/AccessPoint/Location/${locationId}`,
        method: 'GET'
      })
    }),
    makeDefaultLocation: builder.mutation<
      null,
      { isOwner?: boolean; ownerId?: number; locationId: number }
    >({
      query: (data) => ({
        url: `/api/Location/SetDefaultLocation/${data.locationId}`,
        method: 'PUT',
        body: {
          isOwner: data.isOwner,
          ownerId: data.ownerId
        }
      })
    }),
    resetLocation: builder.mutation<null, string[]>({
      query: (locationIds) => ({
        url: `/api/Location/ResetLocation`,
        method: 'POST',
        body: {
          locationIds: locationIds.join(',')
        }
      })
    }),
    deleteAccessPoints: builder.mutation<null, number[]>({
      query: (accessPointIds) => ({
        url: `/api/AccessPoint/RemoveAccessPoints`,
        method: 'POST',
        body: {
          AccessPointIds: accessPointIds
        }
      })
    }),
    setLocationBrand: builder.mutation<
      null,
      { locationId: number; brandId: number | null }
    >({
      query: ({ locationId, brandId }) => ({
        url: 'api/Location/Brand',
        method: 'PUT',
        body: { locationId, brandId }
      })
    }),
    makeDefaultLocationByUser: builder.mutation<
      null,
      { locationId: number; userId: number }
    >({
      query: (data) => ({
        url: `api/Location/SetDefaultLocation`,
        method: 'PUT',
        body: { locationId: data.locationId, userId: data.userId }
      })
    }),
    turnOffLocation: builder.mutation<null, number[]>({
      query: (locationIds) => ({
        url: `api/Location/Off`,
        method: 'POST',
        body: {
          locationIds
        }
      })
    }),
    turnOnLocation: builder.mutation<null, number[]>({
      query: (locationIds) => ({
        url: `api/Location/On`,
        method: 'POST',
        body: {
          locationIds
        }
      })
    }),
    syncMissingUserLocation: builder.mutation<null, number>({
      query: (userId) => ({
        url: `api/Location/MissingUserLocation`,
        method: 'POST',
        body: { userId }
      })
    }),
    deleteLocationByRole: builder.mutation<
      null,
      { locationId: number; userId?: number }[]
    >({
      query: (payload) => ({
        url: 'api/Location/ByAdmin',
        method: 'DELETE',
        body: payload
      })
    }),
    resetAllTablesVersion: builder.mutation<null, number>({
      query: (locationId) => ({
        url: `/api/Location/ResetAllTablesVersion/${locationId}`,
        method: 'PUT'
      })
    })
  })
});

export const {
  useGetLocationsMutation,
  useGetFoldersMutation,
  useAddFoldersMutation,
  useGetLocationsUsersByLocationIdMutation,
  useAddUserToLocationMutation,
  useDeleteUserFromLocationMutation,
  useGetLocationsByLoggedUserMutation,
  useGetLocationDetailsByLocationIdMutation,
  useGetUserLastLocationsMutation,
  usePostUserSelectLocationMutation,
  useDeleteLocationMutation,
  useGetFlaggedLocationsMutation,
  useGetLoggedUserLocationsAndOrganizationsMutation,
  useShareLocationWithTargetsMutation,
  useUpdateLocationLatLongMutation,
  useGetLocationsAndOrganizationsByUserMutation,
  useIncrementAllTablesVersionMutation,
  useGetLocationTypesMutation,
  useUpdateLocationTypeMutation,
  useGetAccessPointsByLocationMutation,
  useMakeDefaultLocationMutation,
  useResetLocationMutation,
  useDeleteAccessPointsMutation,
  useSetLocationBrandMutation,
  useMakeDefaultLocationByUserMutation,
  useTurnOffLocationMutation,
  useTurnOnLocationMutation,
  useSyncMissingUserLocationMutation,
  useDeleteLocationByRoleMutation,
  useResetAllTablesVersionMutation
} = apiLocations;
